// Tokens for the Figma frame "Inventory / Mobile - Modal" (4038:30453) - page "PAVEL DRAFT",
// section "Inventory". Drawn on the same 390-wide mobile frame as the rest of the redesign, with
// a 358-wide modal panel, so every number in this folder is in that design space and is scaled
// once by the screen.
//
// The colours are the file's own variables, read with get_variable_defs off the modal; the
// gradients and glows are the values its Tab / Modal and Tab / Category masters paint.
import { EVENT_FONT } from '../Event/eventTheme';
import { DESKTOP_TWIN_FACTOR } from '../Modals/useRedesignScale';

export const INVENTORY_FONT = EVENT_FONT;

export const DESIGN_FRAME_WIDTH = 390;
/**
 * "Modal / Inventory" (4038:30469) sits at 16/77 in the 390 frame and is drawn 690 tall, but it is
 * one of three tabs - Profile / Inventory / Friends - and the profile's own artboard
 * ("Profile / Modal — Full content", 4022:37722) puts the same panel at 16/44 and 853 tall. The
 * panel must not jump as the tabs switch, so the profile's geometry wins for all three and the
 * bodies here take the height that leaves them.
 */
export const PANEL_WIDTH = 358;
export const PANEL_HEIGHT = 853;
export const PANEL_X = 16;
export const PANEL_Y = 44;
/** Whatever is left under the panel on a short viewport. */
export const PANEL_BOTTOM_GAP = 16;
/**
 * The scale the three tabs share, so the panel never jumps as they switch. On desktop it follows
 * the desktop artboards (the 1.25x twins) but shrinks until the whole 853-tall panel fits the
 * window, never below 0.9 - under that the bodies scroll instead.
 */
export const TABBED_MODAL_SCALE = {
	fitHeight: PANEL_HEIGHT,
	desktopFactor: DESKTOP_TWIN_FACTOR,
	minFitScale: 0.9,
} as const;
/** Wider viewports centre the panel rather than stretching it, same cap as the rest. */
export const MAX_SCALE = 1.15;

export const inventoryColors = {
	/** text/primary - an active tab's label. */
	primary: '#FFFFFF',
	/** text/secondary - every resting label. */
	secondary: '#D9C4BC',
	/** text/accent */
	accent: '#FFFF99',
	/** text/muted */
	muted: '#A88E86',
	/** gold/50 - the item prices. */
	gold50: '#FFF5B9',
	/** surface/raised - the item cells. The panel itself is unfilled. */
	surface: '#330E07',
	/** border/red - every resting tab. */
	border: '#6B1A10',
	/** The panel's own outline - the same bright gold the arcade cards carry. */
	panelBorder: '#FFF908',
	/** The active tab's brighter outline. */
	borderActive: '#FF6A4D',
	/** The item cells' resting hairline - the duller orange, not the tabs' red. */
	itemBorder: '#DA600E',
	/** The equipped cell's bright hairline, which carries the glow with it. */
	itemBorderEquipped: '#FCF17B',
	/** progress-bar-level-green - the "Equipped" line. */
	equipped: '#4ABF54',
	/** "Scrim" (4038:30455) - a warm near-black at 45%, not a neutral one. */
	scrim: 'rgba(11,2,1,0.45)',
} as const;

/** radius/md for the tabs, var(--vip-radius-card) for the item cells, 16 for the panel. */
export const RADIUS_MD = 8;
export const RADIUS_CARD = 10;
export const RADIUS_PANEL = 16;

/** Tab / Modal at rest. */
export const TAB_FILL: Array<[string, string]> = [
	['0', '#330200'],
	['1', '#120000'],
];
/** Tab / Category at rest - a slightly warmer ramp than the modal tabs. */
export const CATEGORY_FILL: Array<[string, string]> = [
	['0', '#330B05'],
	['1', '#0F0402'],
];
/** Both tab families share this fill when active. */
export const ACTIVE_FILL: Array<[string, string]> = [
	['0', '#E0230F'],
	['1', '#8C0D05'],
];

const glow = (color: string, opacity: number, radius: number) =>
	({
		shadowColor: color,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: opacity,
		shadowRadius: radius,
		elevation: Math.round(radius * 0.5),
	}) as const;

/**
 * The file describes the active state as "the paired shadow (#FF0000 55% r6 + #FF7200 45% r16)".
 * React Native takes one shadow per view, so this is the wider, warmer half of the pair - the one
 * that carries the look.
 */
export const inventoryGlow = {
	active: glow('#FF7200', 0.45, 16),
	panel: glow('#FF7200', 0.35, 10),
} as const;
